import { useState } from 'react';
import { supabaseAuth } from '../supabaseClient';

export interface PasswordPolicy {
  minLength: boolean;
  maxLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumberOrPunctuation: boolean;
  noSpacesOrUnicode: boolean;
}

export interface CreateAccountFormData {
  username: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

export interface CreateAccountState {
  formData: CreateAccountFormData;
  error: string;
  loading: boolean;
  accountCreated: boolean;
}

export const useCreateAccount = () => {
  const [state, setState] = useState<CreateAccountState>({
    formData: {
      username: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    },
    error: '',
    loading: false,
    accountCreated: false
  });

  // Phone validation function
  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return false;
    // More comprehensive phone validation
    // Accepts: +1 555 123 4567, +1-555-123-4567, (555) 123-4567, etc.
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    return phoneRegex.test(phone);
  };

  // Password policy validation
  const validatePassword = (password: string): PasswordPolicy => ({
    minLength: password.length >= 8,
    maxLength: password.length <= 18,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumberOrPunctuation: /[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    noSpacesOrUnicode: !/[\s\u0080-\uFFFF]/.test(password)
  });

  const passwordPolicy = validatePassword(state.formData.password);
  const isPasswordValid = Object.values(passwordPolicy).every(Boolean);
  const passwordsMatch = state.formData.password === state.formData.confirmPassword && state.formData.confirmPassword !== '';
  const isPhoneValid = validatePhoneNumber(state.formData.phone);

  const updateFormData = (field: keyof CreateAccountFormData, value: string) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value
      },
      error: '' // Clear error when user starts typing
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!isPasswordValid) {
      setState(prev => ({ ...prev, error: 'Password does not meet all requirements.' }));
      return;
    }

    if (!passwordsMatch) {
      setState(prev => ({ ...prev, error: 'Passwords do not match.' }));
      return;
    }

    if (!state.formData.email) {
      setState(prev => ({ ...prev, error: 'Email is required.' }));
      return;
    }

    if (!isPhoneValid) {
      setState(prev => ({ ...prev, error: 'Please enter a valid phone number.' }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: '' }));
    
    try {
      // Create account using Supabase
      const { data, error } = await supabaseAuth.auth.signUp({
        email: state.formData.email,
        password: state.formData.password,
        options: {
          data: {
            username: state.formData.username,
            phone: state.formData.phone,
          }
        }
      });

      if (error) {
        throw error;
      }

      if (data.user) {
        setState(prev => ({ 
          ...prev, 
          accountCreated: true,
          loading: false 
        }));
        console.log('Account created successfully:', data.user);
      }
    } catch (err: any) {
      setState(prev => ({ 
        ...prev, 
        error: err.message || 'Failed to create account. Please try again.',
        loading: false 
      }));
    }
  };

  const resetForm = () => {
    setState({
      formData: {
        username: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      },
      error: '',
      loading: false,
      accountCreated: false
    });
  };

  return {
    // State
    formData: state.formData,
    error: state.error,
    loading: state.loading,
    accountCreated: state.accountCreated,
    
    // Computed values
    passwordPolicy,
    isPasswordValid,
    passwordsMatch,
    isPhoneValid,
    
    // Actions
    updateFormData,
    handleSubmit,
    resetForm
  };
}; 